import moment from 'moment';
import { createSelector } from 'reselect';
import { calucateTotalPriceForSectors, getProp } from '../../utilities';

const enterStationsDomainSelector = state => state.enterStations;
const stepperDomainSelector = state => state.stepper;
const toStationsDomainSelector = state => state.stationTo;
const connectionDomainSelector = state => state.connection;
const paramsDomainSelector = state => state.connectionParams;

export const enterStationsIsFetchingSelector = createSelector(
  enterStationsDomainSelector,
  substate => getProp(substate, ['isFetching'], false),
);

export const enterStationsListSelector = createSelector(
  enterStationsDomainSelector,
  substate => {
    const stations = getProp(substate, ['stations'], []);
    return stations.map(station => {
      return {
        value: station.from_station,
        label: station.name,
      };
    });
  },
);

export const toStationsIsFetchingSelector = createSelector(
  toStationsDomainSelector,
  substate => getProp(substate, ['isFetching'], false),
);

export const getSelectedStation = createSelector(
  toStationsDomainSelector,
  substate => getProp(substate, ['selected'], null),
);

export const toStationsListSelector = createSelector(
  toStationsDomainSelector,
  getSelectedStation,
  (substate, selected) => {
    const stations = getProp(substate, ['byFromStationId'], {});
    if (selected) {
      return stations[selected].map(station => {
        return {
          value: station.to_station,
          label: station.name,
        };
      });
    }
    return [];
  },
);

const connectionDataSelector = createSelector(
  connectionDomainSelector,
  substate => getProp(substate, ['data']),
);

export const connectionByIdSelector = createSelector(
  connectionDomainSelector,
  substate => getProp(substate, ['byId']),
);

export const connectionResultsSelector = createSelector(
  connectionDataSelector,
  connectionByIdSelector,
  (substate, byId) => {
    const connections = substate.reduce((prev, item) => {
      return [...prev, byId[item]];
    }, []);
    let shortest = null;
    let cheapest = null;
    let cheapestIndexs = [];
    let shortestIndexs = [];
    connections.forEach((item, index) => {
      // najlacnejsie
      const itemPrice = calucateTotalPriceForSectors(
        getProp(item, ['sectors'], []),
      );
      if (itemPrice !== null) {
        if (cheapest === null) {
          cheapest = itemPrice;
          cheapestIndexs.push(index);
        } else if (itemPrice < cheapest) {
          cheapest = itemPrice;
          cheapestIndexs = [index];
        } else if (itemPrice === cheapest) {
          cheapestIndexs.push(index);
        }
      }
      // najkradsie
      const diff = moment.utc(
        moment(item.datetime_dest).diff(moment(item.datetime)),
      );
      if (shortest === null) {
        shortest = diff;
        shortestIndexs.push(index);
      } else if (diff < shortest) {
        shortest = diff;
        shortestIndexs = [index];
      } else if (diff === shortest) {
        shortestIndexs.push(index);
      }
    });
    if (connections.length > 0) {
      cheapestIndexs.forEach(i => {
        connections[i].cheapest = true;
      });
      shortestIndexs.forEach(i => {
        connections[i].shortest = true;
      });
    }

    return connections;
  },
);

export const connectionIsFetchingSlector = createSelector(
  connectionDomainSelector,
  substate => getProp(substate, ['isFetching'], true),
);

export const getSorterParamsSelector = createSelector(
  connectionDomainSelector,
  substate => getProp(substate, ['sorter'], {}),
);

export const getWay = createSelector(connectionDomainSelector, substate =>
  getProp(substate, ['way'], 1),
);

export const isDirectParamSelector = createSelector(
  paramsDomainSelector,
  substate => substate.direct === '1',
);

export const paramsSelector = createSelector(
  paramsDomainSelector,
  enterStationsListSelector,
  toStationsListSelector,
  (params, fromList, toList) => {
    return {
      from: fromList.find(
        i => i.value === getProp(params, ['stationFrom'], 0).toString(),
      ),
      to: toList.find(
        i => i.value === getProp(params, ['stationTo'], 0).toString(),
      ),
      count: getProp(params, ['count'], 1),
      ticketType: getProp(params, ['ticketType']),
    };
  },
);

export const connectionsSorterSelector = way => {
  return createSelector(
    connectionResultsSelector,
    getSorterParamsSelector,
    isDirectParamSelector,
    paramsSelector,
    (substate, sorter, isDirect, params) => {
      let array = [...substate];
      if (substate.length > 0) {
        // zistujem ci chcem iba priame spojenia
        if (isDirect || params.ticketType === 'B') {
          // ak sa jedna o bookovanie spiatocnej cesty, nesmieme zobrazit moznost prepinat priame/nepriame spojenia
          // chceme len priame spojenia - mailova komunikacia, p. Tongel, 16.10.2018
          array = array.filter(connection => {
            if (connection.sectors.length === 1) {
              return true;
            }
            return false;
          });
        }
        array = array.filter(s => s.way === way.toString()).sort((a, b) => {
          if (sorter.value === 'duration') {
            const diffA = moment.duration(
              moment(a.datetime).diff(moment(a.datetime_dest)),
            );
            const diffB = moment.duration(
              moment(b.datetime).diff(moment(b.datetime_dest)),
            );
            return diffA < diffB;
          } else if (sorter.value === 'price') {
            return (
              calucateTotalPriceForSectors(getProp(a, ['sectors'], [])) >
              calucateTotalPriceForSectors(getProp(b, ['sectors'], []))
            );
          }
          return [];
        });
      }
      return array;
    },
  );
};

export const connectionsObjectByDateSelector = way => {
  return createSelector(connectionsSorterSelector(way), connections => {
    const objectByDate = connections.reduce((prev, c) => {
      const date = moment.parseZone(c.datetime).format('YYYY-MM-DD');
      return { ...prev, [date]: [...getProp(prev, [date]), c] };
    }, {});

    return Object.keys(objectByDate).reduce((p, date) => {
      return {
        ...p,
        [date]: objectByDate[date].sort((a, b) => a.datetime > b.datetime),
      };
    }, {});
  });
};

// detail
const connectionDetailDomainSelector = state => state.connectionDetail;

export const connectionDetailSelector = createSelector(
  connectionDetailDomainSelector,
  substate =>
    getProp(substate, ['connectionsById'], {})[
      getProp(substate, ['selectedConnection'], null)
    ],
);

export const connectionDetailIsFetchingSelector = createSelector(
  connectionDetailDomainSelector,
  substate => {
    return getProp(substate, ['isFetching'], true);
  },
);

// sectory vsetky podla id
export const sectorsOfAllConnectionsByIdSelector = createSelector(
  connectionByIdSelector,
  connections => {
    return Object.keys(connections).reduce((prev, connection) => {
      return {
        ...prev,
        ...getProp(connections[connection], ['sectors'], []).reduce(
          (p, sector) => {
            return { ...p, [sector.id_dps]: sector };
          },
          {},
        ),
      };
    }, {});
  },
);

// isSeating pre konkretne spojenie
export const isSeatingForIdConnection = id => {
  return createSelector(connectionByIdSelector, data => {
    const sectors = getProp(data, [id, 'sectors'], []);
    let isSeating = false;
    sectors.forEach(sector => {
      if (sector.seating) {
        isSeating = true;
      }
    });
    return isSeating;
  });
};

export const selectedConnectionDetail = createSelector(
  connectionByIdSelector,
  stepperDomainSelector,
  (byId, stepper) => {
    const connectionId = getProp(stepper, [
      'stepParams',
      'firstWay',
      'connectionId',
    ]);
    return byId[connectionId];
  },
);

export const dateOfFirstWaySelector = createSelector(
  selectedConnectionDetail,
  connectionDetail => getProp(connectionDetail, ['datetime']),
);

export const nextConnectionDateSelector = way => {
  return createSelector(connectionDomainSelector, substate => {
    if (way === 1) {
      return getProp(substate, ['nextWay1'], null);
    } else if (way === 2) {
      return getProp(substate, ['nextWay2'], null);
    }
    return null;
  });
};

export const prevConnectionDateSelector = way => {
  return createSelector(connectionDomainSelector, substate => {
    if (way === 1) {
      return getProp(substate, ['prevWay1'], null);
    } else if (way === 2) {
      return getProp(substate, ['prevWay2'], null);
    }
    return null;
  });
};

export const isFetchingNextSelector = createSelector(
  connectionDomainSelector,
  substate => getProp(substate, ['isFetchingNext']),
);

export const isFetchingPrevSelector = createSelector(
  connectionDomainSelector,
  substate => getProp(substate, ['isFetchingPrev']),
);



// WEBPACK FOOTER //
// ./src/containers/Connections/selectors.js